import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";
import { accesField, accesInputField } from "./acces.js";
import { CONVERSATION_TYPES, ancreDAppSchema, cleSchema } from "./conversation-cle.js";

/**
 * Conversation : famille "fil d'echanges", la premiere famille PORTEUSE d'un
 * acces par fiche (`acces.ts`, `docs/socle/acces-par-fiche.md`).
 *
 * UN objet unique, specialise par `type` (vocabulaire de l'app, pas du
 * socle) : un sujet avec Lia, un canal public, un message direct a deux ou a
 * quelques personnes, un canal prive, le chatter d'une fiche. C'est le modele
 * du centre de communication (note de conception maquette-hub, section 1).
 * Les messages vivent dans `messages`, avec `conversationId`.
 *
 * Ce qui fait la difference entre ces usages, c'est l'ACCES :
 *   - un canal public : `acces: { visibilite: 'equipe' }` ;
 *   - un message direct, un canal prive : `prive`, ses participants ;
 *   - le chatter d'une fiche : `ancre` sur cette fiche (lisible par qui lit la
 *     fiche, jamais stocke en liste de participants).
 *
 * Convention de patron (cf. asset) : `conversationCoreFields`,
 * `ConversationSchema`, `ConversationCreateInputSchema`.
 */

export const CONVERSATION_NAME_MAX = 200;
export const CONVERSATION_DESCRIPTION_MAX = 2000;

/** Le type d'une conversation : FERME (contrat du 05/10/2026). Le sujet Lia reste dans `liaConversations`. */
export const conversationTypeSchema = z.enum(CONVERSATION_TYPES);
export type ConversationType = z.infer<typeof conversationTypeSchema>;

/** Champs metier de la famille conversation (hors champs standard du socle). */
export const conversationCoreFields = {
  orgId: z.string(),
  /** `canal` (public ou prive), `direct` (quelques personnes), `chatter` (attache a un objet). */
  type: conversationTypeSchema,
  /** Le nom affiche. Vide pour un message direct ou un chatter (l'ecran nomme les participants ou l'objet). */
  name: z.string().max(CONVERSATION_NAME_MAX),
  /** Ce dont parle la conversation. Optionnel. */
  description: z.string().max(CONVERSATION_DESCRIPTION_MAX).optional(),
  /**
   * La CLE DE RECHERCHE d'un `direct` ou d'un `chatter` (`conversation-cle.ts`) :
   * `direct:p1|p2`, `fiche:contacts/abc`, `porte:timesheet/journal`, `app:pages`.
   * Unique par org, immuable ; l'id du document en derive (`conversationIdDe`).
   * Un canal n'en a pas.
   */
  cle: cleSchema.optional(),
  /**
   * L'ANCRE DESCRIPTIVE d'un chatter de porte ou d'app (acces equipe : la porte
   * suffit). Le chatter d'une FICHE n'en a pas : son ancre est dans `acces`.
   */
  ancre: ancreDAppSchema.optional(),
} as const;

/** Schema complet de la famille conversation (champs standard + metier + acces). */
export const ConversationSchema = z.object({
  ...baseEntityFields,
  ...conversationCoreFields,
  ...accesField,
});

export type Conversation = z.infer<typeof ConversationSchema>;

/**
 * Ce que l'appelant fournit a la creation. `acces` absent vaut equipe ; un
 * acces prive recoit son proprietaire du SDK.
 */
export const ConversationCreateInputSchema = z.object({
  ...conversationCoreFields,
  name: conversationCoreFields.name.default(""),
  ...accesInputField,
  ...customFieldsField,
});

export type ConversationCreateInput = z.infer<typeof ConversationCreateInputSchema>;

/**
 * Le patch de mise a jour. `orgId` et `type` sont immuables ; `acces` ne se
 * modifie que par `partager`, `retirer` et `quitter` (rules).
 */
export const ConversationUpdateInputSchema = z
  .object({ ...conversationCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true, cle: true, ancre: true })
  .partial();

export type ConversationUpdateInput = z.infer<typeof ConversationUpdateInputSchema>;
